import { useInjectReducer } from "utils/@reduxjs/useInjectReducer";
import { useInjectSaga } from "utils/@reduxjs/useInjectSaga";
import { createSlice } from "@reduxjs/toolkit";
import { use, useMemo } from "react";
import { usersSaga } from "./saga";

// Helper function to update user status in the users array
const updateUserStatus = (users, userId, newStatus) => {
  const usersCopy = users || [];
  const userIndex = usersCopy.findIndex((user) => user.id === userId);

  if (userIndex !== -1) {
    const updatedUsers = [...usersCopy];
    updatedUsers[userIndex] = {
      ...updatedUsers[userIndex],
      status: newStatus
    };
    return updatedUsers;
  }

  return usersCopy;
};

// create this as a separate function and reuse in deactivate success as well
const updateStatusCounts = (statusMeta, fromStatus, toStatus) => {
  if (statusMeta) {
    if (statusMeta[toStatus] !== undefined) {
      statusMeta[toStatus] += 1;
    }
    if (statusMeta[fromStatus] !== undefined) {
      statusMeta[fromStatus] -= 1;
    }
  }
};

export const initialState = {
  usersInfo: {
    loading: false,
    error: null,
    users: [],
    pagination: {},
    statusMeta: {},
    statusDropdownCounts: [],
    domainUserCounts: {}
  },
  user: {
    loading: false,
    error: null,
    data: null
  },
  userActivateInfo: {
    loading: false,
    error: null,
    success: false,
    user_id: null,
    updateType: null,
    userStatus: null
  },
  userDeactivateInfo: {
    loading: false,
    error: null,
    success: false,
    user_id: null,
    updateType: null,
    userStatus: null
  },
  verifyEmailAndActivateInfo: {
    loading: false,
    error: null,
    success: false,
    user_id: null,
    updateType: null
  },

  updateUserInfo: {
    loading: false,
    error: null,
    success: false
  },

  searchHistoryInfo: {
    loading: false,
    error: null,
    history: [],
    pagination: {}
  },

  profileUplaodInfo: {
    loading: false,
    error: null,
    success: false,
    profile_image: null
  },

  creatorAnalyticsInfo: {
    loading: false,
    error: null,
    data: null
  },
  creatorChartInfo: {
    loading: false,
    error: null,
    data: []
  },

  marketplaceAnalyticsInfo: {
    loading: false,
    error: null,
    data: null
  },
  marketplaceChartInfo: {
    loading: false,
    error: null,
    data: []
  },
  companyUsersByStatusInfo: {
    loading: false,
    error: null,
    users: []
  }
};

export const UsersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    // --- Users ---//
    fetchAllUsers: (state, action) => {
      const payload = action.payload || {};
      state.usersInfo.loading = true;
      state.usersInfo.error = null;
      if (payload.status) state.usersInfo.status = payload.status;
    },
    fetchAllUsersSuccess: (state, action) => {
      const { users, pagination, statusMeta } = action.payload;
      state.usersInfo.loading = false;
      state.usersInfo.users = users;
      state.usersInfo.pagination = pagination;
      state.usersInfo.statusMeta = statusMeta;
      state.usersInfo.statusDropdownCounts =
        action.payload.statusDropdownCounts;
      state.usersInfo.domainUserCounts = action.payload.domainUserCounts;
    },
    fetchAllUsersFailure: (state, action) => {
      state.usersInfo.loading = false;
      state.usersInfo.error = action.payload;
    },

    fetchUserById: (state, action) => {
      state.user.loading = true;
      state.user.error = null;
    },
    fetchUserByIdSuccess: (state, action) => {
      state.user.loading = false;
      state.user.data = action.payload;
    },
    fetchUserByIdFailure: (state, action) => {
      state.user.loading = false;
      state.user.error = action.payload;
    },

    userActivate: (state, action) => {
      state.userActivateInfo.loading = true;
      state.userActivateInfo.error = null;
      state.userActivateInfo.success = false;
      state.userActivateInfo.user_id = action.payload.user_id;
      state.userActivateInfo.updateType = action.payload.updateType || null;
      state.userActivateInfo.userStatus = action.payload.userStatus || null;
    },
    userActivateSuccess: (state, action) => {
      state.userActivateInfo.loading = false;
      state.userActivateInfo.success = true;

      const { updateType, user_id } = state.userActivateInfo;
      if (updateType === "usersList") {
        state.usersInfo.users = updateUserStatus(
          state.usersInfo.users,
          user_id,
          "activated"
        );
        updateStatusCounts(
          state.usersInfo.statusMeta,
          state.userActivateInfo.userStatus,
          "activated"
        );
      } else {
        if (state.user.data?.id === user_id) {
          state.user.data.status = "activated";
        }
      }
    },
    userActivateFailure: (state, action) => {
      state.userActivateInfo.loading = false;
      state.userActivateInfo.error = action.payload;
    },

    userDeactivate: (state, action) => {
      state.userDeactivateInfo.loading = true;
      state.userDeactivateInfo.error = null;
      state.userDeactivateInfo.success = false;
      state.userDeactivateInfo.user_id = action.payload.user_id;
      state.userDeactivateInfo.updateType = action.payload.updateType || null;
      state.userDeactivateInfo.userStatus = action.payload.userStatus || null;
    },
    userDeactivateSuccess: (state, action) => {
      state.userDeactivateInfo.loading = false;
      state.userDeactivateInfo.success = true;

      const { updateType, user_id } = state.userDeactivateInfo;
      if (updateType === "usersList") {
        state.usersInfo.users = updateUserStatus(
          state.usersInfo.users,
          user_id,
          "deactivated"
        );
        updateStatusCounts(
          state.usersInfo.statusMeta,
          state.userDeactivateInfo.userStatus,
          "deactivated"
        );
      } else {
        if (state.user.data?.id === user_id) {
          state.user.data.status = "deactivated";
        }
      }
    },
    userDeactivateFailure: (state, action) => {
      state.userDeactivateInfo.loading = false;
      state.userDeactivateInfo.error = action.payload;
    },
    verifyEmailAndActivate: (state, action) => {
      state.verifyEmailAndActivateInfo.loading = true;
      state.verifyEmailAndActivateInfo.error = null;
      state.verifyEmailAndActivateInfo.success = false;
      state.verifyEmailAndActivateInfo.user_id = action.payload.user_id;
      state.verifyEmailAndActivateInfo.updateType =
        action.payload.updateType || null;
    },
    verifyEmailAndActivateSuccess: (state, action) => {
      state.verifyEmailAndActivateInfo.loading = false;
      state.verifyEmailAndActivateInfo.success = true;

      const { updateType, user_id } = state.verifyEmailAndActivateInfo;
      if (updateType === "usersList") {
        state.usersInfo.users = updateUserStatus(
          state.usersInfo.users,
          user_id,
          "activated"
        );
        updateStatusCounts(
          state.usersInfo.statusMeta,
          "not_filled",
          "activated"
        );
      } else {
        if (state.user.data?.id === user_id) {
          state.user.data.status = "activated";
        }
      }
    },
    verifyEmailAndActivateFailure: (state, action) => {
      state.verifyEmailAndActivateInfo.loading = false;
      state.verifyEmailAndActivateInfo.error = action.payload;
    },
    userUpdate: (state, action) => {
      state.updateUserInfo.loading = true;
      state.updateUserInfo.error = null;
      state.updateUserInfo.success = false;
    },
    userUpdateSuccess: (state, action) => {
      state.updateUserInfo.loading = false;
      state.updateUserInfo.success = true;

      const updatedUser = action.payload.user;
      // Update the user details if it's the same user
      if (state.user.data?.id === updatedUser.id) {
        state.user.data = updatedUser;
      }
    },
    userUpdateFailure: (state, action) => {
      state.updateUserInfo.loading = false;
      state.updateUserInfo.error = action.payload;
      state.updateUserInfo.success = false;
    },

    getSearchHistory: (state, action) => {
      state.searchHistoryInfo.loading = true;
      state.searchHistoryInfo.error = null;
    },
    getSearchHistorySuccess: (state, action) => {
      state.searchHistoryInfo.loading = false;
      state.searchHistoryInfo.history = action.payload.history;
      state.searchHistoryInfo.pagination = action.payload.pagination;
    },
    getSearchHistoryFailure: (state, action) => {
      state.searchHistoryInfo.loading = false;
      state.searchHistoryInfo.error = action.payload;
      state.searchHistoryInfo.pagination = {};
    },

    uploadProfileImage: (state, action) => {
      state.profileUplaodInfo.loading = true;
      state.profileUplaodInfo.error = null;
      state.profileUplaodInfo.success = false;
      state.profileUplaodInfo.profile_image = null;
    },
    uploadProfileImageSuccess: (state, action) => {
      state.profileUplaodInfo.loading = false;
      state.profileUplaodInfo.success = true;
      state.profileUplaodInfo.profile_image = action.payload.profile_image;
    },
    uploadProfileImageFailure: (state, action) => {
      state.profileUplaodInfo.loading = false;
      state.profileUplaodInfo.error = action.payload;
      state.profileUplaodInfo.success = false;
    },

    fetchCreatorAnalytics: (state) => {
      state.creatorAnalyticsInfo.loading = true;
      state.creatorAnalyticsInfo.error = null;
    },
    fetchCreatorAnalyticsSuccess: (state, action) => {
      state.creatorAnalyticsInfo.loading = false;
      state.creatorAnalyticsInfo.data = action.payload.data;
    },
    fetchCreatorAnalyticsFailure: (state, action) => {
      state.creatorAnalyticsInfo.loading = false;
      state.creatorAnalyticsInfo.error = action.payload;
    },

    fetchCreatorChart: (state) => {
      state.creatorChartInfo.loading = true;
      state.creatorChartInfo.error = null;
    },
    fetchCreatorChartSuccess: (state, action) => {
      state.creatorChartInfo.loading = false;
      state.creatorChartInfo.data = action.payload.data;
    },
    fetchCreatorChartFailure: (state, action) => {
      state.creatorChartInfo.loading = false;
      state.creatorChartInfo.error = action.payload;
    },

    fetchMarketplaceAnalytics: (state) => {
      state.marketplaceAnalyticsInfo.loading = true;
      state.marketplaceAnalyticsInfo.error = null;
    },
    fetchMarketplaceAnalyticsSuccess: (state, action) => {
      state.marketplaceAnalyticsInfo.loading = false;
      state.marketplaceAnalyticsInfo.data = action.payload.data;
    },
    fetchMarketplaceAnalyticsFailure: (state, action) => {
      state.marketplaceAnalyticsInfo.loading = false;
      state.marketplaceAnalyticsInfo.error = action.payload;
    },

    fetchMarketplaceChart: (state) => {
      state.marketplaceChartInfo.loading = true;
      state.marketplaceChartInfo.error = null;
    },
    fetchMarketplaceChartSuccess: (state, action) => {
      state.marketplaceChartInfo.loading = false;
      state.marketplaceChartInfo.data = action.payload.data;
    },
    fetchMarketplaceChartFailure: (state, action) => {
      state.marketplaceChartInfo.loading = false;
      state.marketplaceChartInfo.error = action.payload;
    },

    fetchCompanyUsersByStatus: (state) => {
      state.companyUsersByStatusInfo.loading = true;
      state.companyUsersByStatusInfo.error = null;
      state.companyUsersByStatusInfo.users = [];
    },

    fetchCompanyUsersByStatusSuccess: (state, action) => {
      state.companyUsersByStatusInfo.loading = false;
      state.companyUsersByStatusInfo.users = action.payload.users || [];
    },

    fetchCompanyUsersByStatusFailure: (state, action) => {
      state.companyUsersByStatusInfo.loading = false;
      state.companyUsersByStatusInfo.error = action.payload;
      state.companyUsersByStatusInfo.users = [];
    }
  }
});

export const useUsersSlice = () => {
  useInjectReducer(UsersSlice.name, UsersSlice.reducer);
  useInjectSaga(UsersSlice.name, usersSaga);
  const actions = useMemo(() => UsersSlice.actions, []);
  return { actions };
};

export const { actions: usersActions } = UsersSlice;
export default UsersSlice.reducer;
